import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Popover,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  TextField,
} from '@mui/material';
import {
  HelpOutlineOutlined as HelpIcon,
  Lock as LockIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { PyrenzAccordionInput } from '@client/theme';
import { Utils } from '@client/utility';
import { GetUserUUID } from '@components';
import { useUserStore } from '@client/store';

interface PrivateModel {
  model_name: string;
  model_description: string;
}

interface ModelOption {
  value: string;
  label: string;
  description: string;
  tier: 'free' | 'cheap' | 'standard' | 'premium' | 'ultra';
  slug: string;
  isPrivate?: boolean;
}

interface ModelSelectionProps {
  preferredModel: string;
  setPreferredModel: (value: string) => void;
  isModelPrivate: (slug: string) => boolean;
  privateModels: Record<string, PrivateModel>;
}

export function ModelSelection({
  preferredModel,
  setPreferredModel,
  isModelPrivate,
  privateModels,
}: ModelSelectionProps) {
  const userStore = useUserStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState('');
  const [modelOptions, setModelOptions] = useState<ModelOption[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: modelIdentifiers } = await Utils.db.select<{
          name: string;
          is_paid: boolean;
          model_description: string;
          slug: string;
        }>({
          tables: 'model_identifiers',
          columns: 'name, is_paid, model_description, slug',
        });

        const formattedModelOptions: ModelOption[] = modelIdentifiers.map(
          (model) => ({
            value: model.slug,
            label: model.name,
            description: model.model_description,
            tier: model.is_paid ? 'premium' : 'free',
            slug: model.slug,
          })
        );

        setModelOptions(formattedModelOptions);
      } catch (error) {
        console.error('Error fetching model data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
    description: string
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
    setPopoverContent(description);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const mergedModels: ModelOption[] = useMemo(() => {
    const privateModelOptions: ModelOption[] = Object.entries(privateModels).map(
      ([key, value]) => ({
        value: key,
        label: value.model_name,
        description: value.model_description,
        tier: 'premium' as const,
        slug: key,
        isPrivate: true,
      })
    );

    return [
      ...(modelOptions ?? []),
      ...privateModelOptions.filter(
        (pm) => !(modelOptions ?? []).some((opt) => opt.slug === pm.slug)
      ),
    ];
  }, [modelOptions, privateModels]);

  const filteredModels = useMemo(() => {
    if (!searchQuery) return mergedModels;
    return mergedModels.filter((model) =>
      model.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, mergedModels]);

  const getTierDisplay = (tier: string) => {
    switch (tier) {
      case 'free':
        return 'Free';
      case 'premium':
        return 'Premium';
      case 'cheap':
        return 'Cheap';
      case 'standard':
        return 'Standard';
      case 'ultra':
        return 'Ultra';
      default:
        return tier;
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <PyrenzAccordionInput label="Preferred Model">
        {loading || mergedModels.length === 0 ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              placeholder="Search models..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            />
            {filteredModels.map((option) => {
              const isCurrentlySelected = option.slug === preferredModel;
              const isBackendSelected = userStore.modelIdentifiers.some(
                (model) => model.slug === option.slug && model.is_selected
              );
              const isSelected = isCurrentlySelected || isBackendSelected;
              const isPrivate = isModelPrivate(option.slug);

              return (
                <Paper
                  key={option.slug}
                  onClick={() => setPreferredModel(option.slug)}
                  elevation={isSelected ? 6 : 2}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: isSelected ? '#90EE90' : '#444',
                    backgroundColor: isSelected ? '#1a2a1a' : '#1e1e1e',
                    color: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                      borderColor: '#add8e6',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: isSelected ? 600 : 400 }}
                    >
                      {option.label.length > 20
                        ? option.label.slice(0, 20) + '...'
                        : option.label}
                    </Typography>
                    {isPrivate && (
                      <LockIcon fontSize="small" sx={{ ml: 1 }} />
                    )}
                    {isSelected && (
                      <CheckIcon
                        fontSize="small"
                        sx={{ ml: 1, color: '#90EE90' }}
                      />
                    )}
                    <Typography variant="caption" color="gray" sx={{ ml: 2 }}>
                      Tier: {getTierDisplay(option.tier)}
                    </Typography>
                    {isBackendSelected && !isCurrentlySelected && (
                      <Typography
                        variant="caption"
                        sx={{
                          ml: 1,
                          color: '#90EE90',
                          fontSize: '0.7rem',
                        }}
                      >
                        (Selected)
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    onMouseEnter={(e) =>
                      handlePopoverOpen(e, option.description)
                    }
                    onMouseLeave={handlePopoverClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePopoverOpen(e, option.description);
                    }}
                    size="small"
                    sx={{ color: '#fff' }}
                  >
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Paper>
              );
            })}
          </Box>
        )}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ pointerEvents: 'none' }}
          disableRestoreFocus
        >
          <Box p={2}>{popoverContent}</Box>
        </Popover>
      </PyrenzAccordionInput>
    </Box>
  );
}